import React, { useState, useRef, useContext, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import saveIcon from "../../assets/save_icon.png";
import SectionDivider from "../../components/SectionDivider";
import Loader from "../../components/Loader";
import AreYouSureModal from "../../components/AreYouSureModal";
import ProductOrderList from "./purchases/ProductOrderList";
import SelectedProductOrderList from "./purchases/SelectedProductOrderList";
import InformationBox from "../../components/InformationBox";
import PrintableProductOrderView from "./purchases/PrintableProductOrder";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import axiosClient from "../../services/axiosClient";
import PrintableSuppliesBooks from "./PrintaleSupplieBooks";
import { UserAuth } from "../../utils/AuthContext";

function findOutOfStockItems(data) {
  const outOfStockItems = [];

  // Iterate through each category
  data.forEach((category) => {
    // Check each book in the category
    category.books.forEach((book) => {
      const requestedQty = parseInt(book.quantity) || 0;
      const availableStock = book.amountInStock || 0;

      // If requested quantity is more than available stock
      if (requestedQty > availableStock) {
        outOfStockItems.push({
          id: book.id,
          name: book.name,
          category: category.category,
          academicLevel: book.academicLevel,
          requestedQuantity: requestedQty,
          availableStock: availableStock,
          shortage: requestedQty - availableStock,
          productSubType: book.productSubType,
          price: book.purchasePrice,
        });
      }
    });
  });

  return outOfStockItems;
}

export default function SupplieBooks({
  booksData = [],
  setBooksData,
  receiptNumber,
  student = null,
  selectedCurrency,
  onSuccess,
  orderDetails: defaultOrderDetails,
  loadingBooks,
  kassa,
}) {
  const { t } = useTranslation("general");
  const printRef = useRef(null);
  const [orderDetails, setOrderDetails] = useState(defaultOrderDetails);

  // STATES
  const [selectedBooks, setSelectedBooks] = useState({});
  const [allSelectedBooks, setAllSelectedBooks] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [notes, setNotes] = useState("");
  const [showAreYouSure, setAreYouSure] = useState(false);

  // Order creation states
  const [confirmOrderLoading, setConfirmOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const { userData } = useContext(UserAuth);

  // Book headers configuration
  const bookHeaders = [
    { key: "checkbox", label: "choose", type: "checkbox" },
    { key: "name", label: "bookName", type: "text" },
    { key: "productSubType", label: "bookType", type: "text" },
    { key: "price", label: "sellPrice", type: "text" },
    { key: "amountInStock", label: "amountInStock", type: "text" },
    {
      key: "quantity",
      label: "quantity",
      type: "input",
      unit: "book",
      component: CustomInputWithUnit,
    },
  ];

  /* HANDLER FUNCTIONS */
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
    @page {
      size: A4;
      margin: 5mm;
      width: 21cm;
      height: 29.7cm;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        padding: 10mm;
      }
      .printable-order-view { display: flex !important; }
    }
  `,
  });

  // Validation function to check if all selected books have quantities
  const validateSelectedBooks = () => {
    const errors = [];

    allSelectedBooks.forEach((category) => {
      category.books.forEach((book) => {
        const quantity = parseInt(book.quantity) || 0;
        if (quantity <= 0) {
          errors.push(`${book.name} (${category.category})`);
        }
      });
    });

    return errors;
  };

  // Check if order can be confirmed
  const canConfirmOrder = () => {
    if (allSelectedBooks.length === 0) return false;

    return allSelectedBooks.every((category) =>
      category.books.every((book) => {
        const quantity = parseInt(book.quantity) || 0;
        return quantity > 0;
      })
    );
  };

  const handleConfirmOrder = () => {
    const validationErrors = validateSelectedBooks();

    if (validationErrors.length > 0) {
      const errorMessage =
        t("pleaseProvideQuantityFor") + ":\n" + validationErrors.join("\n");
      alert(errorMessage);
      return;
    }

    setAreYouSure(true);
  };

  // Order creation function
  const handleCreateOrder = async () => {
    setConfirmOrderLoading(true);
    setOrderError(null);
    if (!kassa) return;

    try {
      const orders = allSelectedBooks
        .map((category) =>
          category.books.map((book) => ({
            productId: book.id,
            quantity: parseInt(book.quantity) || 0,
          }))
        )
        .flat();

      const orderData = {
        receiptNumber,
        date: orderDetails.date || new Date().toISOString(),
        items: orders,
        kassaId: kassa,
        productType: "book",
        studentId: student.id,
        notes,
        userId: Number(userData?.userId),
      };
      const response = await axiosClient.post("/sell", orderData);

      setOrderDetails({
        ...orderDetails,
        createdBy: response.data.creator?.username,
        companyName: response.data.creator?.organization?.organizationName,
      });

      setIsPrinting(true);
    } catch (error) {
      console.error("Error creating order:", error);
      setOrderError(t("errorSupplieingBooks"));
    } finally {
      setConfirmOrderLoading(false);
    }
  };

  // Book selection handler
  const handleBookSelection = (categoryId, bookId, isSelected) => {
    setSelectedBooks((prev) => {
      if (bookId === null) {
        // Remove entire category
        const { [categoryId]: removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          [bookId]: isSelected,
        },
      };
    });

    if (bookId === null) {
      // Remove entire category
      setAllSelectedBooks((prev) =>
        prev.filter((item) => item.category !== categoryId)
      );
    } else {
      if (isSelected) {
        // Add book to selected list
        const category = booksData.find((cat) => cat.category === categoryId); // Use allBooksData to ensure we can find the book
        if (category) {
          const book = category.books.find((b) => b.id === bookId);
          if (book) {
            setAllSelectedBooks((prev) => {
              const categoryIndex = prev.findIndex(
                (cat) => cat.category === categoryId
              );

              if (categoryIndex >= 0) {
                // Category exists, add book to it
                const bookExists = prev[categoryIndex].books.some(
                  (b) => b.id === bookId
                );
                if (!bookExists) {
                  const updatedCategories = [...prev];
                  updatedCategories[categoryIndex] = {
                    ...updatedCategories[categoryIndex],
                    books: [
                      ...updatedCategories[categoryIndex].books,
                      { ...book },
                    ],
                  };
                  return updatedCategories;
                }
                return prev;
              } else {
                // New category
                return [
                  ...prev,
                  {
                    id: category.id,
                    category: categoryId,
                    books: [{ ...book }],
                  },
                ];
              }
            });
          }
        }
      } else {
        // Remove book from selected list
        setAllSelectedBooks((prev) => {
          const updatedCategories = prev.map((cat) => {
            if (cat.category === categoryId) {
              return {
                ...cat,
                books: cat.books.filter((book) => book.id !== bookId),
              };
            }
            return cat;
          });

          // Remove categories with no books
          return updatedCategories.filter((cat) => cat.books.length > 0);
        });
      }
    }
  };

  // Update input values for the selected books
  const handleInputChange = (categoryId, bookId, field, value) => {
    setAllSelectedBooks((prev) => {
      return prev.map((category) => {
        if (category.category === categoryId) {
          return {
            ...category,
            books: category.books.map((book) => {
              if (book.id === bookId) {
                const updatedBook = {
                  ...book,
                  [field]: value || 0,
                };

                // Auto-calculate total price when quantity changes
                if (field === "quantity") {
                  updatedBook.totalPrice =
                    (updatedBook.price || 0) * (value || 0);
                }

                return updatedBook;
              }
              return book;
            }),
          };
        }
        return category;
      });
    });
  };

  // Reset data after successful order
  const resetToOriginalData = () => {
    setAllSelectedBooks([]);
    setSelectedBooks([]);
    setAreYouSure(false);
    onSuccess?.();
    setIsPrinting(false);
  };

  const outOfStockItems = findOutOfStockItems(allSelectedBooks || []);

  const isConfirmDiabled =
    confirmOrderLoading ||
    isPrinting ||
    !canConfirmOrder() ||
    student === null ||
    outOfStockItems.length > 0;

  useEffect(() => {
    if (isPrinting) {
      handlePrint();
      resetToOriginalData();
    }
  }, [orderDetails]);

  useEffect(() => {
    setOrderDetails(defaultOrderDetails);
  }, [defaultOrderDetails]);

  return (
    <>
      <SectionDivider title={t("theBooks")} />
      {loadingBooks && <Loader />}
      {!loadingBooks && (
        <ProductOrderList
          data={booksData}
          selectedItems={selectedBooks}
          onItemSelect={handleBookSelection}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
          allSelectedItems={allSelectedBooks}
          headers={bookHeaders}
          groupByField="category"
          itemsField="books"
          itemKeyField="id"
          groupLabelPrefix="academicLevel"
        />
      )}
      {allSelectedBooks.length > 0 && (
        <SectionDivider title={t("selectedBooks")} />
      )}
      {outOfStockItems.length > 0 && (
        <InformationBox>
          <div>
            <p>{t("outOfStockWarning")}</p>
            <p className="o-column o-row--gap-size_300 u-margin--top_400">
              {outOfStockItems.map((item, index) => (
                <div key={index}>
                  <strong>{item.name}:</strong> {t("requested")}:{"  "}{" "}
                  {item.requestedQuantity}, {t("available")}:{" "}
                  {item.availableStock}
                </div>
              ))}
            </p>
          </div>
        </InformationBox>
      )}
      {allSelectedBooks.length > 0 &&
        !canConfirmOrder() &&
        student !== null && (
          <InformationBox>
            <p>{t("pleaseProvideQuantityForAllSelectedBooks")} </p>
          </InformationBox>
        )}
      {!loadingBooks && (
        <SelectedProductOrderList
          data={allSelectedBooks}
          setSelectedItems={setAllSelectedBooks}
          currency={selectedCurrency}
          selectedItems={selectedBooks}
          onItemSelect={handleBookSelection}
          onInputChange={handleInputChange}
          headers={bookHeaders}
          groupByField="category"
          itemsField="books"
          itemKeyField="id"
          groupLabelPrefix="academicLevel"
          showDeleteButton={true}
          deleteButtonLabel="delete"
        />
      )}

      {allSelectedBooks.length > 0 && (
        <CustomInput
          title={`${t("notes")}: `}
          type="textarea"
          onChange={(e) => {
            setNotes(e.target.value);
          }}
        />
      )}

      {orderError && (
        <div
          className="error-message"
          style={{ color: "red", margin: "10px 0" }}
        >
          {orderError}
        </div>
      )}

      <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
        {allSelectedBooks.length > 0 && (
          <>
            <CustomButton
              title={t("confirm")}
              onClick={handleConfirmOrder}
              icon={saveIcon}
              iconSize={16}
              disabled={isConfirmDiabled}
              style={{
                opacity: isConfirmDiabled ? 0.6 : 1,
                cursor: isConfirmDiabled ? "not-allowed" : "pointer",
              }}
            />
          </>
        )}
      </div>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => setAreYouSure(false)}
          onConfirm={handleCreateOrder}
          customMessage={t("confirmOrderMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm={true}
          confirmButtonVariant={null}
          error={orderError}
          loading={confirmOrderLoading}
          confirmIconType="whiteCheck"

        />
      )}

      <PrintableSuppliesBooks
        ref={printRef}
        data={allSelectedBooks}
        currency={selectedCurrency}
        orderDetails={orderDetails}
      />
    </>
  );
}
